import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconTags } from '@tabler/icons-react';
import useUpdateClassCoachRole from 'hooks/classCoachRole/useUpdateClassCoachRole';
import { KiloClassCoachRole } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalEditClassCoachRoleProps {
  opened: boolean;
  handleClose: () => void;
  classCoachRole: KiloClassCoachRole;
}

interface FormValues {
  name: string;
  kiloClassCoachRoleId: string;
}

const ModalEditClassCoachRole = ({
  opened,
  handleClose,
  classCoachRole,
}: IModalEditClassCoachRoleProps): JSX.Element => {
  const updateMutation = useUpdateClassCoachRole();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      kiloClassCoachRoleId: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (values: FormValues): void => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        successNotification(
          'Updating coach role for class success',
          `${values.name} role has been successfully updated.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Update coach role for class failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    form.setValues(classCoachRole);
  }, [classCoachRole]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Edit Coach Role For Classes</Title>}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Role Name'
            data-cy='editClassCoachRoleTextInput'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={updateMutation.isLoading}>
              Cancel
            </Button>
            <Button disabled={updateMutation.isLoading} type='submit' data-cy='saveEditClassCoachRoleButton'>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalEditClassCoachRole;
